// CampaignsList.jsx
// /campaigns — lists all saved campaigns as reusable templates.
// Campaigns are Dan's marketing drafts: a tagline + expiry + landing page
// content + optional pancake attachment, reusable as a batch print template
// for event promos, coffee-shop drops, or surprise pancake gifts.

import { useEffect, useState } from "react";
import {
  listCampaigns, createCampaign, duplicateCampaign, archiveCampaign, deleteCampaign,
} from "../../utils/campaigns";

import { STAFF_THEME, FONT } from "../../theme/staff";
const B = STAFF_THEME;

export default function CampaignsList({ navigate }) {
  const [campaigns, setCampaigns] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setCampaigns(await listCampaigns());
  }
  useEffect(() => { refresh(); }, []);

  async function handleNew() {
    if (busy) return;
    setBusy(true);
    try {
      const id = await createCampaign();
      navigate(`/campaigns/${id}`);
    } finally { setBusy(false); }
  }

  async function handleDuplicate(id) {
    setBusy(true);
    try {
      const newId = await duplicateCampaign(id);
      navigate(`/campaigns/${newId}`);
    } finally { setBusy(false); }
  }

  async function handleArchiveToggle(id, archived) {
    setBusy(true);
    try { await archiveCampaign(id, !archived); await refresh(); }
    finally { setBusy(false); }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Delete "${name}" permanently? Any printed tickets from this campaign will still work but you won't be able to print more.`)) return;
    setBusy(true);
    try { await deleteCampaign(id); await refresh(); }
    finally { setBusy(false); }
  }

  const visible = (campaigns || []).filter(c => showArchived ? true : !c.archived);

  return (
    <div style={{ minHeight: "100vh", background: B.dark, color: "white", fontFamily: FONT.body, padding: "1rem", boxSizing: "border-box" }}>

      {/* Header — ← Menu pinned right for consistency across the app. */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        <div style={{ fontSize: "1.1rem", fontWeight: 800, color: B.gold }}>🎟 Campaigns</div>
        <div style={{ flex: 1 }} />
        <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: B.muted, fontSize: "0.78rem", cursor: "pointer" }}>
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
          Show archived
        </label>
        <button onClick={handleNew} disabled={busy} style={{ ...primaryBtn(), opacity: busy ? 0.5 : 1 }}>+ New campaign</button>
        <button onClick={() => navigate("/menu")} style={hud()}>← Menu</button>
      </div>

      {/* Intro — visible when empty */}
      {campaigns !== null && visible.length === 0 && (
        <div style={{ background: B.panel, border: `1px dashed ${B.border}`, borderRadius: "12px", padding: "1.5rem", textAlign: "center", color: B.muted, fontSize: "0.88rem", lineHeight: 1.55 }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>✨</div>
          <div style={{ color: B.cream, fontWeight: 700, fontSize: "1rem", marginBottom: "0.35rem" }}>No campaigns yet</div>
          <p style={{ margin: "0 auto", maxWidth: "420px" }}>
            Campaigns are reusable templates for printing Golden Tickets — QR codes
            you can hand out, leave in conspicuous places, or gift with a pancake.
            Save one, duplicate it for the next event, track who's scanning.
          </p>
          <button onClick={handleNew} disabled={busy} style={{ ...primaryBtn(), marginTop: "1rem" }}>+ Create your first campaign</button>
        </div>
      )}

      {/* List */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {visible.map(c => (
          <CampaignCard
            key={c.id}
            campaign={c}
            onOpen={() => navigate(`/campaigns/${c.id}`)}
            onDuplicate={() => handleDuplicate(c.id)}
            onArchiveToggle={() => handleArchiveToggle(c.id, c.archived)}
            onDelete={() => handleDelete(c.id, c.name)}
            busy={busy}
          />
        ))}
      </div>
    </div>
  );
}

function CampaignCard({ campaign, onOpen, onDuplicate, onArchiveToggle, onDelete, busy }) {
  const c = campaign;
  return (
    <div style={{
      background: c.archived ? "rgba(45,16,32,0.6)" : B.panel,
      border: `1px solid ${B.border}`,
      borderRadius: "14px",
      padding: "0.9rem 1rem",
      opacity: c.archived ? 0.65 : 1,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
        <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={onOpen}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", flexWrap: "wrap" }}>
            <div style={{ color: "white", fontWeight: 800, fontSize: "1rem" }}>{c.name || "Untitled"}</div>
            {c.linkedOrderId && <span style={pill(B.gold)}>📎 pancake attached</span>}
            {c.archived && <span style={pill(B.dim)}>archived</span>}
          </div>
          {c.headline && <div style={{ color: B.cream, fontSize: "0.82rem", marginTop: "0.2rem" }}>{c.headline}</div>}
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", marginTop: "0.4rem", color: B.muted, fontSize: "0.72rem" }}>
            <span>🖨 {c.totalPrinted || 0} printed</span>
            <span>👁 {c.totalScanned || 0} scanned</span>
            {c.ticketExpiryText && <span>⌛ {c.ticketExpiryText}</span>}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", flexShrink: 0 }}>
          <button onClick={onOpen}        disabled={busy} style={secondaryBtn()}>Edit</button>
          <button onClick={onDuplicate}   disabled={busy} style={secondaryBtn()}>Duplicate</button>
          <button onClick={onArchiveToggle} disabled={busy} style={secondaryBtn()}>{c.archived ? "Unarchive" : "Archive"}</button>
          {c.archived && (
            <button onClick={onDelete} disabled={busy} style={{ ...secondaryBtn(), borderColor: "#c0392b", color: "#e74c3c" }}>Delete</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── STYLE HELPERS ──
function hud() {
  return { background: "transparent", border: `1px solid ${B.border}`, color: B.muted, borderRadius: "8px", padding: "0.35rem 0.7rem", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" };
}
function primaryBtn() {
  return { background: B.gold, color: B.dark, border: "none", borderRadius: "10px", padding: "0.55rem 0.95rem", fontSize: "0.88rem", fontWeight: 800, cursor: "pointer" };
}
function secondaryBtn() {
  return { background: "transparent", border: `1px solid ${B.border}`, color: B.muted, borderRadius: "8px", padding: "0.3rem 0.7rem", fontSize: "0.72rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" };
}
function pill(color) {
  return { background: "rgba(255,255,255,0.08)", border: `1px solid ${color}`, color, borderRadius: "99px", padding: "0.1rem 0.5rem", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" };
}
